/**
 * Provenance — always-on inline data-origin badge.
 *
 * Honest-state companion: marks WHERE a rendered value comes from so an
 * operator (or reviewer) can tell real backend/API data apart from
 * client-derived, fabricated, or absent values at a glance.
 *
 *   live   — real, authoritative data (backend query, media manifest,
 *            QoE beacon). Renders NO badge; real data needs no marker.
 *   local  — real but derived client-side, not authoritative (e.g. the
 *            player's own preview QoE measured from the <video> element).
 *   mock   — fabricated / placeholder with no real source behind it
 *            (e.g. the sprite-hover scrubber thumbnails).
 *   absent — the backend/API returned null/empty; the UI shows "—". The
 *            value is honest, but there is no data behind it.
 *
 * Every wrapped node also carries a `data-source="<kind>"` attribute so a
 * future dev-overlay sweep can highlight them programmatically.
 *
 * PRESENTATION lives in `styles/global.css` (the `[data-source]`,
 * `[data-prov-badge]`, `[data-mock]`, `[data-mock-ribbon]`, `[data-decorative]`
 * rules). These components only emit the custom attributes + content, so a
 * marked node carries one attribute instead of a repeated inline-style block.
 */
import React from 'react';

export type SourceKind = 'live' | 'local' | 'mock' | 'absent';

// To avoid clutter the on-screen marker is a single compact "?" icon (amber,
// distinct from the app's red error states); the human word ("Simulado" /
// "Local" / "Sin dato") and the longer explanation live in the hover tooltip.
const MARKER_GLYPH = '?';
const LABELS: Record<Exclude<SourceKind, 'live'>, { word: string; title: string }> = {
  mock:   { word: 'Simulado', title: 'Dato simulado — no proviene de ninguna API real.' },
  local:  { word: 'Local',    title: 'Medición local de este navegador — no es la telemetría autoritativa del backend.' },
  absent: { word: 'Sin dato',  title: 'Sin dato del backend (campo nulo). El valor mostrado es honesto, pero no hay dato detrás.' },
};

function Badge({ kind, detail }: { kind: Exclude<SourceKind, 'live'>; detail?: string }) {
  const s = LABELS[kind];
  // Tooltip leads with the word ("Sin dato" / "Simulado"), then the detail.
  const tip = detail ? `${s.word} — ${detail}` : `${s.word} — ${s.title}`;
  // The explanation rides on `data-tip` (rendered as an instant styled popup by
  // the `[data-prov-badge]:hover::after` rule in global.css) instead of the
  // sluggish native `title`. It is also folded into `aria-label` for SR users.
  return (
    <span
      data-prov-badge=""
      data-tip={tip}
      aria-label={`origen del dato (${kind}): ${tip}`}
    >
      {MARKER_GLYPH}
    </span>
  );
}

/**
 * Wrap a value to append its provenance badge inline. `live` passes the
 * children through untouched (no badge).
 */
export function Provenance({
  kind, detail, children, style,
}: {
  kind: SourceKind;
  detail?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  if (kind === 'live') return <>{children}</>;
  return (
    <span data-source={kind} style={style}>
      {children}
      <Badge kind={kind} detail={detail} />
    </span>
  );
}

/** Standalone badge for tight spots where there is no child to wrap. */
export function ProvenanceBadge({ kind, detail }: { kind: SourceKind; detail?: string }) {
  if (kind === 'live') return null;
  return <span data-source={kind}><Badge kind={kind} detail={detail} /></span>;
}

/**
 * MockBoundary — container-level marker for a WHOLE panel/table/section whose
 * data is wholesale fabricated/seeded (vs. the inline `Provenance` chip for a
 * single value). Draws a faint amber dashed outline + a corner "SIMULADO"
 * ribbon. Uses `outline` (not `border`) so it adds no layout/spacing shift.
 * Always-on. Carries `data-mock="panel"` for programmatic sweeps.
 *
 * Usage: wrap the panel —  <MockBoundary detail="…why it's fake…"><Panel …/></MockBoundary>
 */
export function MockBoundary({
  label = 'SIMULADO', detail, children, style, radius = 14,
}: {
  label?: string;
  detail?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  radius?: number;
}) {
  return (
    <div
      data-mock="panel"
      style={{ ...(radius !== 14 ? { ['--mock-radius' as any]: `${radius}px` } : null), ...style }}
    >
      <span
        data-mock-ribbon=""
        data-tip={`${label} — ${detail || 'esta sección no proviene de una API real.'}`}
        aria-label={`sección con datos simulados: ${detail || 'esta sección no proviene de una API real.'}`}
      >
        {MARKER_GLYPH}
      </span>
      {children}
    </div>
  );
}
